import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  Inject,
  InjectionToken,
  Input,
  OnChanges,
  OnInit,
  Optional,
  SimpleChanges,
  ViewEncapsulation
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  Validators
} from '@angular/forms';
import { AutoUnsubscribe } from '@nitro/core/common';

import { DateTimeAdapter } from '../adapters/date-time-adapter';
import { NkDateTimeBase, NkTimeConfig } from '../base/time-edit-base';
import { TimeoutWrapper } from 'libs/common-lib/src/lib/providers/timeout.wrapper/timeout.wrapper';
import { debounceTime } from 'rxjs/operators';

export interface NkTimeEditConfig extends NkTimeConfig {
  showMeridian?: boolean;
  showMilliseconds?: boolean;
  showSeconds?: boolean;
  wheel?: boolean;
}
export const NK_TIME_EDIT_DEFAULT_OPTIONS = new InjectionToken<NkTimeEditConfig>(
  'NK_TIME_EDIT_DEFAULT_OPTIONS'
);

@AutoUnsubscribe()
@Component({
  selector: 'nk-time-edit',
  templateUrl: './time-edit.html',
  styleUrls: ['./time-edit.scss'],
  exportAs: 'NkTimeEdit',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NkTimeEdit),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => NkTimeEdit),
      multi: true
    }
  ],
  host: {
    class: 'nk-time'
  },
  encapsulation: ViewEncapsulation.None
})
export class NkTimeEdit<D> extends NkDateTimeBase<D> implements OnInit, OnChanges {
  timeParts: FormGroup;
  _editing = false;
  _isDisabled = false;
  _isPM = false;
  _wheel = false;

  private _digits(value: any, digits = 2) {
    return ('' + Number(value || 0)).padStart(digits, '0').slice(-1 * digits);
  }

  // TODO: Fix the accessor error created after updating to TS 4.0.0
  @Input()
  get disabled() {
    return this._isDisabled;
  }
  set disabled(d: boolean) {
    this._isDisabled = d;
    if (this.timeParts) {
      d ? this.timeParts.disable() : this.timeParts.enable();
    }
  }

  _onFocus() {
    this._editing = true;
  }

  _onFocusOut() {
    this._editing = false;
  }

  _onInput(event: any) {
    this._editing = true;
    const target = <HTMLInputElement>event.target;
    if (target.value.length > target.maxLength) {
      target.value = this._digits(target.value, target.maxLength || 2);
      this.timeParts.get(target.getAttribute('formControlName') || '')?.setValue(target.value);
    }
    this.timeout.add(() => {
      target.focus();
    }, 50);
  }

  _onWheel(event: WheelEvent) {
    event.preventDefault();
    const tgt = <HTMLInputElement>event.target;
    if (this._wheel) {
      this._editing = true;
      let delta = event.deltaY < 0 ? -1 : 1;
      if (event.ctrlKey) {
        delta = delta * 10;
      }
      tgt.value = this._digits(
        Math.min(
          parseInt(tgt.max, 10),
          Math.max(parseInt(tgt.min, 10), parseInt(tgt.value, 10) + delta)
        ),
        tgt.maxLength || 2
      );
      this.timeParts.get(tgt.getAttribute('formControlName')).setValue(tgt.value);
    }
  }

  constructor(
    public _dtAdapter: DateTimeAdapter<D>,
    @Optional()
    @Inject(NK_TIME_EDIT_DEFAULT_OPTIONS)
    public config: NkTimeEditConfig,
    public _changeDetectorRef: ChangeDetectorRef,
    private _el: ElementRef,
    private timeout: TimeoutWrapper
  ) {
    super(_dtAdapter, config, _changeDetectorRef);

    this.timeParts = new FormGroup({
      hh: new FormControl(null, {
        validators: [Validators.required, Validators.max(23)],
        updateOn: 'change'
      }),
      mm: new FormControl(null, {
        validators: [Validators.required, Validators.max(59)],
        updateOn: 'change'
      }),
      ss: new FormControl(null, {
        validators: [Validators.required, Validators.max(59)],
        updateOn: 'change'
      }),
      ms: new FormControl(null, {
        validators: [Validators.required, Validators.max(999)],
        updateOn: 'change'
      })
    });

    this.timeParts.valueChanges.subscribe((v: any) => {
      if (!this.timeParts.invalid) {
        let h = v.hh * 1;
        if (this.showMeridian) {
          if (this._isPM) {
            h = h < 12 ? h + 12 : h;
          } else if (h === 12) {
            h = 0;
          }
        }

        this.setTime(h, v.mm, this.showSeconds ? v.ss : 0, this.showMilliseconds ? v.ms : 0);
        if (this._editing) {
          this._onUserInput(this.value);
        }
        this._editing = false;
      }
    });

    this.timeParts.statusChanges.subscribe(err => {
      this._validationErrors = this.timeParts.invalid ? { nkTimeInputInvalid: true } : null;
      this._onValidatorChange();
    });
  }

  updateControls = v => {
    this._isPM = !v ? false : this._dtAdapter.format(v, 'A') === 'PM';
    const h = v ? this._dtAdapter.getHours(v) : 0;
    this.timeParts.setValue(
      {
        hh: v ? this._digits(this.showMeridian ? ((h + 11) % 12) + 1 : h) : '',
        mm: v ? this._digits(this._dtAdapter.getMinutes(v)) : '',
        ss: v ? this._digits(this._dtAdapter.getSeconds(v)) : '',
        ms: v ? this._digits(this._dtAdapter.getMilliseconds(v), 3) : ''
      },
      { emitEvent: false }
    );
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.showMeridian) {
      this.updateControls(this.value);
    }
  }

  ngOnInit(): void {
    this.config = this.config || {};
    this.showMeridian =
      this.showMeridian === void 0
        ? this.config.showMeridian
        : coerceBooleanProperty(this.showMeridian);
    this.showSeconds =
      this.showSeconds === void 0
        ? this.config.showSeconds
        : coerceBooleanProperty(this.showSeconds);
    this.showMilliseconds =
      this.showMilliseconds === void 0
        ? this.config.showMilliseconds
        : coerceBooleanProperty(this.showMilliseconds);
    this._wheel = this.config.wheel ? this.config.wheel : this._wheel;
    super.ngOnInit();
  }
}
